import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Scheduler, { SchedulerTypes, View } from 'devextreme-react/scheduler'

import dayjs from '@crew/modules/dayjs'
import {
  CrewTaskTooltip,
  TaskTooltip,
} from 'components/elements/crewTaskTooltip/crewTaskTooltip'
import { Task } from '@crew/models/domain'
import { useAppSelector } from 'states/hooks'
import { useParams, useSearchParams } from 'react-router-dom'
import qs from 'qs'
import { getParamAsArray, getParamAsDate, getParamAsString } from 'utils'
import {
  DetailTaskSearchOptions,
  TaskDetailListTabs,
  TaskSearchOptions,
} from 'enums/app'
import { isDate } from '@crew/utils'
import { CrewScheduler } from 'components/devextreme/crewScheduler/crewScheduler'
import { useModal } from 'components/layouts/modal/useModal'
import { useTranslation } from '@crew/modules/i18n'
import { TaskEntryDialog } from 'features/task/components/taskEntryDialog/taskEntryDialog'
import { useCrewNavigate } from 'hooks/useCrewNavigate'
import { getDefaultTabValue } from '@crew/utils/enum'
import { TaskEntryFormMode } from 'features/task/components/taskEntryDialog/components/taskEntryForm/taskEntryForm'
import { SearchTasksParam, useTaskCalendar } from './useTaskCalendar'

type AppointmentType = {
  data: { appointmentData: Task }
}

export const TaskCalendar = memo(() => {
  const schedulerRef = useRef<Scheduler>(null)
  const { tasksDataSource } = useTaskCalendar(schedulerRef)

  const { t } = useTranslation()
  const { navigate } = useCrewNavigate()
  const { projectId } = useParams()

  // タスク登録ダイアログの表示制御
  const [isTaskEntryDialogOpen, openTaskEntryDialog, closeTaskEntryDialog] =
    useModal()

  const [dueDate, setDueDate] = useState<Date>()

  // 表示対象のDate（データ取得対象期間の算出に使用する）
  const [displayTargetDate, setDisplayTargetDate] = useState<Date>(new Date())

  // 検索パラメータの取得
  const [searchParams] = useSearchParams()
  const params = useMemo(
    () => qs.parse(searchParams.toString()),
    [searchParams]
  )

  // 検索パラメータが変更されたときにDataSourceのフィルターを更新・再読み込み
  useEffect(() => {
    if (!schedulerRef.current) return

    // 検索条件を設定
    const filterParams: SearchTasksParam = {
      keyword: getParamAsString(DetailTaskSearchOptions.Keyword.id, params),
      taskKindIds: getParamAsArray(TaskSearchOptions.TaskKindId.id, params),
      assignToUser: getParamAsString(TaskSearchOptions.AssignToUser.id, params),
      taskStateIds: getParamAsArray(TaskSearchOptions.TaskStateId.id, params),
      taskStateTypes: getParamAsArray(
        TaskSearchOptions.TaskStateType.id,
        params
      ),
      taskPriorities: getParamAsArray(
        TaskSearchOptions.TaskPriority.id,
        params
      )?.map((taskPriority) => Number(taskPriority)), // string[] -> number[]
      taskCategoryIds: getParamAsArray(
        TaskSearchOptions.TaskCategoryId.id,
        params
      ),
      projectId: getParamAsString(TaskSearchOptions.ProjectId.id, params),
      projectGroupIds: getParamAsArray(
        TaskSearchOptions.ProjectGroupId.id,
        params
      ),
      startDate: getParamAsDate(TaskSearchOptions.StartDate.id, params),
      dueDate: getParamAsDate(TaskSearchOptions.DueDate.id, params),
      createdById: getParamAsString(TaskSearchOptions.CreatedById.id, params),
      updatedById: getParamAsString(TaskSearchOptions.UpdatedById.id, params),
      createdAt: getParamAsDate(TaskSearchOptions.CreatedAt.id, params),
      updatedAt: getParamAsDate(TaskSearchOptions.UpdatedAt.id, params),
    }

    tasksDataSource.filter(filterParams)
    tasksDataSource.load()
  }, [params, tasksDataSource])

  const taskEventMessage = useAppSelector((state) => state.app.taskEventMessage)

  // タスクの更新・削除が行われたときのみカスタムデータソースをリロード
  useEffect(() => {
    if (taskEventMessage) {
      // データソースがロードされていないと実行時エラーが発生するため処理を中断する
      if (!tasksDataSource.isLoaded()) return

      tasksDataSource.reload()
    }
  }, [taskEventMessage, tasksDataSource])

  // カレンダーセルクリック時のデフォルトツールチップ表示を抑制
  // NOTE: onCellClickでe.cancel = trueとしてもセルクリック時は抑制できるが、
  //       編集ダイアログ表示時になぜかツールチップもセットで表示されてしまうため、
  //       ツールチップ自体の起動タイミングでキャンセルできるonAppointmentFormOpeningを使用
  const handleAppointmentFormOpening = useCallback(
    (event: SchedulerTypes.AppointmentFormOpeningEvent) => {
      event.cancel = true

      const dueDate = event.appointmentData?.dueDate

      // 期日を設定
      setDueDate(dueDate ? dayjs.utc(dueDate).toDate() : undefined)

      // タスク登録ダイアログを開く
      openTaskEntryDialog()
    },
    [openTaskEntryDialog]
  )

  // タスク登録完了
  const handleTaskRegistered = useCallback(
    (taskId: string) => {
      // タスク詳細画面に遷移
      navigate(`/tasks/${taskId}/${getDefaultTabValue(TaskDetailListTabs)}`)

      // 登録ダイアログを閉じる
      closeTaskEntryDialog()
    },
    [navigate, closeTaskEntryDialog]
  )

  // ツールチップクローズ時のハンドラ
  const handleCloseTooltip = useCallback(() => {
    schedulerRef.current?.instance.hideAppointmentTooltip()
  }, [])

  // タスククリック時のツールチップのrender
  const renderAppointmentTooltip = useCallback(
    (item: AppointmentType) => {
      // ツールチップに表示するタスク情報を取得
      const task: Task = item.data.appointmentData

      // ツールチップに表示するデータを設定
      const taskTooltip: TaskTooltip = {
        id: task.id,
        subject: task.subject,
        description: task.description,
        startDate: task.startDate,
        dueDate: task.dueDate,
        assignToUser: task.assignToUser,
        taskPriority: task.taskPriority,
        taskKind: task.taskKind,
        taskState: task.taskState,
        estimatedWorkTimes: task.estimatedWorkTimes,
        actualWorkTimes: task.actualWorkTimes,
        remainingWorkTimes: task.remainingWorkTimes,
      }

      return (
        <CrewTaskTooltip
          data={taskTooltip}
          onCloseTooltip={handleCloseTooltip}
        />
      )
    },
    [handleCloseTooltip]
  )

  // 日付変更イベントハンドラ
  const handleCurrentDateChange = useCallback(
    (e: string | number | Date) => {
      if (isDate(e)) {
        // 表示対象日付のstateを更新する
        setDisplayTargetDate(e)
      }
    },
    [setDisplayTargetDate]
  )

  const [schedulerWidth, setSchedulerWidth] = useState<number>()
  const schedulerContainerRef = useRef<HTMLDivElement>(null)

  // ウィンドウサイズ変更時にスケジューラの幅を更新
  // アンマウント時にクリーンアップを行う必要があるためuseEffectを使用
  useEffect(() => {
    const node = schedulerContainerRef.current
    if (!node) return

    // ResizeObserverを使用してコンテナの幅を監視
    const resizeObserver = new ResizeObserver(() => {
      // スケジューラの幅を更新
      setSchedulerWidth(node.offsetWidth)
    })
    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  // 日付セル内のカスタムレンダー
  // タスク数が多い場合、「その他xxxを選択する」を表示する
  const renderAppointmentCollector = useCallback(
    (data: SchedulerTypes.AppointmentCollectorTemplateData) => {
      return (
        <div className="crew-link">
          {t('label.selectMore', {
            value: data.appointmentCount,
          })}
        </div>
      )
    },
    [t]
  )

  return (
    <div ref={schedulerContainerRef} className="h-full w-full overflow-auto">
      {/* カレンダー表示 */}
      <CrewScheduler
        ref={schedulerRef}
        timeZone={dayjs.tz.guess()}
        dataSource={tasksDataSource}
        defaultCurrentView="month"
        defaultCurrentDate={displayTargetDate}
        height={750}
        width={schedulerWidth}
        textExpr="subject"
        startDateExpr="startDate"
        endDateExpr="dueDate"
        appointmentTooltipComponent={renderAppointmentTooltip}
        onAppointmentFormOpening={handleAppointmentFormOpening}
        onCurrentDateChange={handleCurrentDateChange}
        appointmentCollectorRender={renderAppointmentCollector}
      >
        <View type="month" />
      </CrewScheduler>

      {/* タスク登録ダイアログ */}
      <TaskEntryDialog
        entryFormMode={TaskEntryFormMode.New}
        title={t('label.addTaskTitle')}
        onSubmit={handleTaskRegistered}
        isOpen={isTaskEntryDialogOpen}
        onClose={closeTaskEntryDialog}
        projectId={projectId}
        defaultDueDate={dueDate}
      />
    </div>
  )
})
